/**
 * Parse Axos recording events into typed interaction data buffers.
 *
 * Axos events are plain objects with:
 *   { type: string, when: number, payload: { ... }, eventId: string, segment: number }
 *
 * Kept in sync with:
 *   external-api/src/events/listeners/inference/axosParser.ts
 */

import type { InteractionBuffers } from "./types";

function readNum(obj: Record<string, unknown>, key: string): number {
  return typeof obj[key] === "number" ? (obj[key] as number) : 0;
}

function readStr(obj: Record<string, unknown>, key: string): string {
  return typeof obj[key] === "string" ? (obj[key] as string) : "";
}

function readBool(obj: Record<string, unknown>, key: string): boolean {
  return typeof obj[key] === "boolean" ? (obj[key] as boolean) : false;
}

export function parseAxosEvents(
  events: Record<string, unknown>[],
  buffers: InteractionBuffers,
): void {
  for (const evt of events) {
    const ts = readNum(evt, "when");
    const type = readStr(evt, "type");

    if (ts > 0) {
      if (ts < buffers.sessionStartTimestamp)
        buffers.sessionStartTimestamp = ts;
      if (ts > buffers.latestTimestamp) buffers.latestTimestamp = ts;
    }

    const payload = evt.payload as Record<string, unknown> | undefined;
    if (!payload && type !== "selection") continue;

    switch (type) {
      case "meta":
        parseMeta(payload!, buffers);
        break;

      case "mouse":
        buffers.mousePositions.push({
          timestamp: ts,
          x: readNum(payload!, "x"),
          y: readNum(payload!, "y"),
        });
        break;

      case "mouse-click":
        buffers.clicks.push({
          timestamp: ts,
          x: readNum(payload!, "x"),
          y: readNum(payload!, "y"),
        });
        break;

      case "scroll":
        buffers.scrolls.push({
          timestamp: ts,
          y: readNum(payload!, "scrollTop"),
        });
        break;

      case "keyboard":
        parseKeyboard(payload!, ts, buffers);
        break;

      case "focus-element":
        buffers.focusBlurs.push({
          timestamp: ts,
          type: readBool(payload!, "isFocused") ? "focus" : "blur",
        });
        break;

      case "mutation":
      case "node-patch":
        buffers.mutations.push({ timestamp: ts });
        break;

      case "stylesheet-snapshot":
      case "stylesheet-mutation":
      case "stylesheet-removed":
      case "stylesheet-patch":
        buffers.styleSheets.push({ timestamp: ts });
        break;

      case "selection":
        buffers.selections.push({ timestamp: ts });
        break;

      default:
        break;
    }
  }
}

function parseMeta(
  payload: Record<string, unknown>,
  buffers: InteractionBuffers,
): void {
  const w = readNum(payload, "width");
  const h = readNum(payload, "height");
  if (w > 0 && h > 0) {
    buffers.viewport = { width: w, height: h };
  }
}

function parseKeyboard(
  payload: Record<string, unknown>,
  ts: number,
  buffers: InteractionBuffers,
): void {
  if (readStr(payload, "kind") !== "keydown") return;

  buffers.inputs.push({
    timestamp: ts,
    text: readStr(payload, "key"),
  });
}
